<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { saveAs } from 'file-saver';
import { DateRangePicker, PrimaryButton, useSnackbarStore } from '@es-bill/es-bill-common-components';
import BillrunsDetailsResultsTable from '@/components/BillrunsDetailsResultsTable.vue';
import BillrunsEnergyDataRequestDialog from '@/components/BillrunsEnergyDataRequestDialog.vue';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import { useMeteringPointMembershipsStore } from '@/stores/meteringPointMembershipsStore';
import { useBillrunsPeriodAnalysisStore } from '@/stores/billrunsPeriodAnalysisStore';
import { exportDetailRowsToCsvBlob, getMaxDateRangeCount } from '@/services/billrunEnergyDataExportsService';
import { buildPeriodAnalysisSummary } from '@/services/billrunsPeriodAnalysisService';
import { useI18n } from 'vue-i18n';

const communitiesStore = useCommunitiesStore();
const membershipsStore = useMeteringPointMembershipsStore();
const analysisStore = useBillrunsPeriodAnalysisStore();
const snackbarStore = useSnackbarStore();
const { t } = useI18n();

const {
  dateRange,
  selectedCommunityEcids,
  billingRelevantMeterCodesOnly,
  rows,
  loading,
  hasFetched,
  error,
  energyRequestPreviewLoading,
  energyRequestPreview,
  submitting,
  submissionProgress,
  invalidPeriod,
  canLoad,
} = storeToRefs(analysisStore);
const energyRequestDialogOpen = ref(false);

const billingRelevantFilterMode = computed({
  get() {
    return billingRelevantMeterCodesOnly.value ? 'billingRelevant' : 'all';
  },
  set(value) {
    analysisStore.setBillingRelevantMeterCodesOnly(value === 'billingRelevant');
  },
});

const communitiesByEcid = computed(() => new Map(
  communitiesStore.allCommunities.filter((c) => c.ecid).map((c) => [c.ecid, c])
));

const communityOptions = computed(() => communitiesStore.allCommunities
  .map((community) => ({
    title: `${community.ecn} - ${community.name}`,
    value: community.ecid,
    ecn: community.ecn,
  }))
  .sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }))
);

const selectedCommunities = computed(() => selectedCommunityEcids.value
  .map((ecid) => communitiesByEcid.value.get(ecid))
  .filter(Boolean)
);

const filteredRows = computed(() => rows.value);
const maxDateRangeCount = computed(() => getMaxDateRangeCount(filteredRows.value));
const summary = computed(() => buildPeriodAnalysisSummary(filteredRows.value));

function problemTypeLabel(row) {
  return row.problemTypeI18nKey
    ? t(`billrunsDetails.${row.problemTypeI18nKey}`)
    : row.problemType;
}

watch([selectedCommunityEcids, dateRange], () => {
  analysisStore.resetResults();
}, { deep: true });

async function loadAnalysis() {
  await analysisStore.fetchAnalysis(selectedCommunities.value);
}

async function openEnergyRequestDialog() {
  const hasRequests = await analysisStore.prepareEnergyRequests(
    communitiesByEcid.value,
    membershipsStore.allMeteringPointMemberships
  );
  if (hasRequests) {
    energyRequestDialogOpen.value = true;
  }
}

function exportCsv() {
  const { blob, fileName } = exportDetailRowsToCsvBlob(filteredRows.value);
  saveAs(blob, fileName.replace('billruns_energy_data_details', 'billruns_period_analysis'));
}

async function confirmEnergyDataRequests() {
  const stats = await analysisStore.submitPreparedEnergyRequests();
  if (!stats) return;
  energyRequestDialogOpen.value = false;
  snackbarStore.showSnackbar(
    stats.callsFailed
      ? t('billrunsAnalysis.requestEnergyDataSubmitPartial')
      : t('billrunsAnalysis.requestEnergyDataSubmitSuccess'),
    t('billrunsAnalysis.requestEnergyDataSubmitMessage', {
      succeeded: stats.callsSucceeded,
      failed: stats.callsFailed,
    }),
    stats.callsFailed ? 'Warning' : 'Success',
    6000,
    []
  );
}
</script>

<template>
  <v-container class="containerBase pa-0">
    <v-container class="containerStandard">
      <h1 class="text-h4 mb-6">{{ $t('billrunsAnalysis.title') }}</h1>

      <v-card class="mb-4" elevation="2">
        <v-card-title><v-icon class="mr-2" color="primary">mdi-cog</v-icon>{{ $t('setup') }}</v-card-title>
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="4">
              <v-autocomplete v-model="selectedCommunityEcids" :items="communityOptions"
                :label="$t('billrunsDashboard.communities')"
                item-title="title" item-value="value" multiple chips closable-chips clearable
                variant="outlined" hide-details bg-color="white" />
            </v-col>
            <v-col cols="12" md="4">
              <DateRangePicker
                :dateRange="dateRange"
                :placeholder="$t('dateRange')"
                @update:dateRange="(value) => dateRange = value"
              />
            </v-col>
            <v-col cols="12" md="2">
              <PrimaryButton :text="$t('billrunsAnalysis.load')" :loading="loading" :disabled="!canLoad" @click="loadAnalysis" />
            </v-col>
          </v-row>
          <v-row align="center">
            <v-col cols="12" class="d-flex align-center ga-2">
              <span class="text-body-2 text-medium-emphasis">
                {{ $t('billrunsDashboard.billingRelevantMeterCodes') }}
                  <v-tooltip activator="parent" location="top">
                    <span style="white-space: pre-line">
                      {{ $t('billrunsDashboard.billingRelevantMeterCodesTooltip') }}
                    </span>
                  </v-tooltip>
              </span>
              <v-btn-toggle
                v-model="billingRelevantFilterMode"
                mandatory
                divided
                density="compact"
                color="primary"
              >
                <v-btn value="billingRelevant" size="small">
                  {{ $t('billrunsDashboard.billingRelevantFilterRelevant') }}
                </v-btn>
                <v-btn value="all" size="small">{{ $t('all') }}</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
          <v-alert v-if="invalidPeriod" type="warning" variant="tonal" class="mt-4">
            {{ $t('billrunsAnalysis.invalidPeriod') }}
          </v-alert>
          <v-row v-if="hasFetched" class="mt-2">
            <v-col cols="12" class="d-flex flex-wrap ga-2">
              <PrimaryButton :text="$t('billrunsDetails.exportCsv')" :disabled="filteredRows.length === 0" @click="exportCsv" />
              <PrimaryButton :text="$t('billrunsDetails.requestEnergyData')"
                :disabled="energyRequestPreviewLoading || filteredRows.length === 0"
                :loading="energyRequestPreviewLoading" @click="openEnergyRequestDialog" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card v-if="hasFetched && !loading && filteredRows.length > 0" class="mb-4" elevation="2">
        <v-card-title><v-icon class="mr-2" color="primary">mdi-chart-box-outline</v-icon>{{ $t('billrunsAnalysis.summaryTitle') }}</v-card-title>
        <v-card-text>
          <div class="d-flex flex-wrap ga-6 mb-4">
            <div>
              <div class="text-caption text-medium-emphasis">{{ $t('billrunsAnalysis.affectedDsos') }}</div>
              <div class="text-h5">{{ summary.dsoCount }}</div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">{{ $t('billrunsAnalysis.affectedMeteringPoints') }}</div>
              <div class="text-h5">{{ summary.meteringPointCount }}</div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">{{ $t('billrunsAnalysis.affectedDateRanges') }}</div>
              <div>{{ summary.dateRanges.join(', ') }}</div>
            </div>
          </div>

          <h3 class="text-subtitle-1 font-weight-bold mb-2">{{ $t('billrunsAnalysis.byProblemType') }}</h3>
          <v-table density="compact" class="summary-table">
            <thead><tr><th>{{ $t('billrunsDetails.columnProblemType') }}</th><th>{{ $t('billrunsAnalysis.affectedMeteringPoints') }}</th><th>{{ $t('billrunsAnalysis.affectedDateRanges') }}</th></tr></thead>
            <tbody>
              <tr v-for="item in summary.byProblemType" :key="item.problemType">
                <td>{{ problemTypeLabel(item) }}</td>
                <td>{{ item.meteringPointCount }}</td>
                <td>{{ item.dateRanges.join(', ') }}</td>
              </tr>
            </tbody>
          </v-table>

          <template v-if="summary.dsoCount > 1">
            <h3 class="text-subtitle-1 font-weight-bold mt-5 mb-2">{{ $t('billrunsAnalysis.byDso') }}</h3>
            <v-table density="compact" class="summary-table">
              <thead><tr><th>{{ $t('billrunsDetails.columnDso') }}</th><th>{{ $t('billrunsAnalysis.affectedMeteringPoints') }}</th><th>{{ $t('billrunsAnalysis.affectedDateRanges') }}</th></tr></thead>
              <tbody>
                <tr v-for="item in summary.byDso" :key="item.key">
                  <td>{{ item.dso }}</td>
                  <td>{{ item.meteringPointCount }}</td>
                  <td>{{ item.dateRanges.join(', ') }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </v-card-text>
      </v-card>

      <v-card elevation="2">
        <v-card-title><v-icon class="mr-2" color="primary">mdi-table</v-icon>{{ $t('billrunsDetails.resultsTitle') }}</v-card-title>
        <v-card-text>
          <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
          <BillrunsDetailsResultsTable :rows="filteredRows" :loading="loading" :analysis-loading="false"
            :has-fetched="hasFetched" :open-billrun-count="hasFetched ? 1 : 0"
            :detail-row-count="rows.length" :max-date-range-count="maxDateRangeCount"
            :is-data-fully-loaded="hasFetched && !loading" />
        </v-card-text>
      </v-card>
    </v-container>

    <BillrunsEnergyDataRequestDialog v-model="energyRequestDialogOpen"
      :summary="energyRequestPreview.summary" :submitting="submitting"
      :submission-progress="submissionProgress" @confirm="confirmEnergyDataRequests"
      @cancel="energyRequestDialogOpen = false" />
  </v-container>
</template>

<style scoped>
.summary-table :deep(th) {
  white-space: nowrap;
}
</style>
